import { notification } from 'antd';
import type { PageableResponse } from '../types';
import type {
  AvailableClassGroupTeacher,
  AvailableClassGroupTeachersRequest,
  ClassGroup,
  ClassGroupsRequest,
  CreateClassGroupBody,
  GenerateClassGroupsBody,
  GenerateClassGroupsResponse,
  UpdateClassGroupBody,
} from './types';
import { api } from '../api';

const listTag = { type: 'ClassGroups' as const, id: 'LIST' };

export const classGroupsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getClassGroups: build.query<PageableResponse<ClassGroup>, ClassGroupsRequest>({
      query: (params = {}) => ({
        url: '/class-groups',
        params: {
          ...params,
          sort: params.sort?.join(','),
        },
      }),
      providesTags: (result) =>
        result
          ? [listTag, ...result.content.map(({ id }) => ({ type: 'ClassGroups' as const, id }))]
          : [listTag],
    }),
    createClassGroup: build.mutation<ClassGroup, CreateClassGroupBody>({
      query: (body) => ({
        url: '/class-groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: [listTag],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({ message: 'Учебная группа создана' });
        } catch {
          // Ошибка API отображается общим baseQuery.
        }
      },
    }),
    updateClassGroup: build.mutation<ClassGroup, UpdateClassGroupBody>({
      query: ({ id, ...body }) => ({
        url: `/class-groups/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [listTag, { type: 'ClassGroups', id }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({ message: 'Учебная группа обновлена' });
        } catch {
          // Ошибка API отображается общим baseQuery.
        }
      },
    }),
    deleteClassGroup: build.mutation<void, number>({
      query: (id) => ({
        url: `/class-groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [listTag, { type: 'ClassGroups', id }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({ message: 'Учебная группа удалена' });
        } catch {
          // Ошибка API отображается общим baseQuery.
        }
      },
    }),
    generateClassGroups: build.mutation<GenerateClassGroupsResponse, GenerateClassGroupsBody>({
      query: (body) => ({
        url: '/class-groups/generate',
        method: 'POST',
        body,
      }),
      invalidatesTags: [listTag],
    }),
    getAvailableClassGroupTeachers: build.query<
      AvailableClassGroupTeacher[],
      AvailableClassGroupTeachersRequest
    >({
      query: (params) => ({
        url: '/class-groups/available-teachers',
        params,
      }),
      transformResponse: (
        response: AvailableClassGroupTeacher[] | AvailableClassGroupTeacher | null,
      ) => (Array.isArray(response) ? response : response ? [response] : []),
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetClassGroupsQuery,
  useCreateClassGroupMutation,
  useUpdateClassGroupMutation,
  useDeleteClassGroupMutation,
  useGenerateClassGroupsMutation,
  useGetAvailableClassGroupTeachersQuery,
} = classGroupsApi;
